import React, { useState } from 'react';
import { ActionIcon, Box, Button, Group, Paper, Text } from '@mantine/core';
import { IconMinus, IconPlus, IconShoppingCart, IconTrash } from '@tabler/icons-react';
import { Product } from '@common/merch';
import { THEME_COLORS } from '../../theme/ThemeConstants';
import { formatPrice } from '../../utilities/Currency';
import { ModelViewer } from '../model/ModelViewer';

interface ProductBanner {
  text: string;
  color: string;
}

interface PackageProps {
  product: Product;
  onAddToCart?: (merchandiseId: string) => void;
  onViewDetails?: (productData: Product) => void;
  isInCart?: boolean;
  quantity?: number;
  lineId?: string;
  onUpdateQuantity?: (lineId: string, quantity: number) => void;
  onRemove?: (lineId: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  banner?: ProductBanner;
}

export const Package: React.FC<PackageProps> = ({
  product,
  onAddToCart,
  onViewDetails,
  isInCart = false,
  quantity = 1,
  lineId,
  onUpdateQuantity,
  onRemove,
  isLoading = false,
  disabled = false,
  banner
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Get the first variant for pricing and adding to cart
  const firstVariant = product.variants?.[0];
  const price = firstVariant ? parseFloat(firstVariant.price.amount) : 0;
  const currency = firstVariant?.price.currencyCode || 'USD';
  // Show a real product photo if there is one; otherwise render the 3D model itself
  // (the Shopify poster has a baked-in white background that clashes with the dark card).
  const realImageUrl = product.images?.[0]?.url;
  const model = product.model3d;
  const soldOut = !product.availableForSale;

  return (
    <Paper
      p={0}
      style={{
        position: 'relative',
        backgroundColor: 'rgba(20, 20, 20, 0.4)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: 8,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)'
      }}
    >
      {banner && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 200,
            height: 200,
            overflow: 'hidden',
            zIndex: 10,
            pointerEvents: 'none'
          }}
        >
          <Box
            style={{
              position: 'absolute',
              top: 32,
              right: -50,
              width: 200,
              backgroundColor: banner.color,
              textAlign: 'center',
              transform: 'rotate(45deg)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              padding: '8px 0'
            }}
          >
            <Text
              size='xs'
              fw={700}
              c='white'
              tt='uppercase'
              style={{
                letterSpacing: '0.05em',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}
            >
              {banner.text}
            </Text>
          </Box>
        </Box>
      )}
      <Box
        style={{
          position: 'relative',
          width: '100%',
          height: 240,
          backgroundColor: 'rgba(15, 15, 15, 0.3)',
          cursor: 'pointer',
          overflow: 'hidden'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => onViewDetails?.(product)}
      >
        <Box
          style={{
            width: '100%',
            height: '100%',
            opacity: soldOut ? 0.45 : 1,
            filter: soldOut ? 'grayscale(0.6)' : 'none',
            transition: 'opacity 0.2s ease'
          }}
        >
          {model?.glbUrl ? (
            <ModelViewer
              compact
              glbUrl={model.glbUrl}
              usdzUrl={model.usdzUrl}
              posterUrl={model.posterUrl}
              cameraDistance={model.cameraDistance}
              alt={product.title}
            />
          ) : realImageUrl ? (
            <img
              src={realImageUrl}
              alt={product.images?.[0]?.altText || product.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease',
                transform: isHovered ? 'scale(1.05)' : 'scale(1)'
              }}
            />
          ) : null}
        </Box>

        <Box
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none'
          }}
        >
          <Box
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: THEME_COLORS.accent.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: isHovered ? 'scale(1)' : 'scale(0.8)',
              transition: 'transform 0.3s ease'
            }}
          >
            <Text size='md' fw={700} c='white' tt='uppercase'>
              VIEW
            </Text>
          </Box>
        </Box>
      </Box>

      <Box p={16}>
        <Group justify='space-between' align='center' mb={16} wrap='nowrap'>
          <Text
            size='md'
            fw={700}
            c='white'
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {product.title}
          </Text>

          <Text
            size='md'
            c={soldOut ? THEME_COLORS.text.dimmed : THEME_COLORS.accent.primary}
            fw={400}
            style={{ flexShrink: 0 }}
          >
            {formatPrice(price, currency)}
          </Text>
        </Group>

        {soldOut ? (
          <Box
            style={{
              height: 37,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4,
              border: '1px solid rgba(255, 255, 255, 0.08)',
              cursor: 'default'
            }}
          >
            <Text size='sm' fw={600} c={THEME_COLORS.text.secondary}>
              Sold Out
            </Text>
          </Box>
        ) : isInCart && lineId ? (
          <Group gap={8} wrap='nowrap' justify='space-between'>
            <Group
              gap={0}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 4,
                border: '1px solid rgba(255, 255, 255, 0.08)',
                overflow: 'hidden'
              }}
            >
              <ActionIcon
                variant='transparent'
                size={37}
                onClick={() => onUpdateQuantity?.(lineId, Math.max(1, quantity - 1))}
                disabled={disabled}
                style={{
                  color: 'white',
                  borderRadius: 0,
                  borderRight: '1px solid rgba(255, 255, 255, 0.08)',
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer'
                }}
              >
                <IconMinus size={20} />
              </ActionIcon>

              <Box
                style={{
                  width: 60,
                  height: 37,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '1px solid rgba(255, 255, 255, 0.08)'
                }}
              >
                <Text size='md' fw={600} c='white'>
                  {quantity}
                </Text>
              </Box>

              <ActionIcon
                variant='transparent'
                size={37}
                onClick={() => onUpdateQuantity?.(lineId, quantity + 1)}
                disabled={disabled}
                style={{
                  color: 'white',
                  borderRadius: 0,
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer'
                }}
              >
                <IconPlus size={20} />
              </ActionIcon>
            </Group>

            <ActionIcon
              variant='filled'
              size={37}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                flexShrink: 0,
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer'
              }}
              onClick={() => onRemove?.(lineId)}
              loading={isLoading}
              disabled={disabled}
            >
              <IconTrash size={20} />
            </ActionIcon>
          </Group>
        ) : (
          <Button
            leftSection={<IconShoppingCart size={24} />}
            variant='filled'
            c={disabled ? THEME_COLORS.text.dimmed : '#ffffff'}
            style={{
              width: '100%',
              backgroundColor: disabled ? 'rgba(255, 255, 255, 0.05)' : THEME_COLORS.accent.orange,
              cursor: disabled ? 'not-allowed' : 'pointer'
            }}
            onClick={() => firstVariant && onAddToCart?.(firstVariant.id)}
            loading={isLoading}
            disabled={disabled || !firstVariant}
          >
            Add To Cart
          </Button>
        )}
      </Box>
    </Paper>
  );
};
